<template>
    <template v-if="showRetirementAge">
        <retirement-age></retirement-age>
    </template>

    <div class="d-none d-md-flex justify-content-center">
        <ol class="stepper" aria-hidden="true">
            <li v-for="(workflowStep, i) in workflowSteps"
                :key="i"
                :class="getStageClass(i + 1, workflowStep)"
                :tabindex="isStageAccessible(i + 1, workflowStep) ? 0 : -1"
                @click="changeLocation($event, workflowStep.resource, isStageAccessible(i + 1, workflowStep))"
                @keyup.enter.prevent="changeLocation($event, workflowStep.resource, isStageAccessible(i + 1, workflowStep))">
                <template v-if="showIcons">
                    <composite-icon :icons="getNavIcon(workflowStep.view)"></composite-icon>
                </template>
                <template v-else>
                    <a class="stepNumber" tabindex="-1" :href="workflowStep.resource">{{ workflowStep.number }}</a>
                </template>
                <span tabindex="-1">{{ workflowStep.text }}</span>
            </li>
        </ol>
    </div>

    <div class="d-block d-md-none">
        <div class="breadcrumb-mobile">
            <span class="breadcrumb-text">{{ workflowStage }}</span>
            <span>
                <span class="breadcrumb-number">{{ getCurrentStage() }}</span>&nbsp;/&nbsp;<span class="breadcrumb-number">{{ stepCount }}</span>
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { isImpersonating } from "@/guiide/ts/utilities";
import { useStore } from "vuex";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import View from "@/guiide/ts/view";
import RetirementAge from "./RetirementAge.vue";
import CompositeIcon from "./CompositeIcon.vue";

const props = defineProps({
    workflowStage: { type: String, required: true }
});

const store = useStore();
const workflowSteps = ref<Array<WorkflowStep>>(null);

enum MenuItemState {
  Enabled = "",
  Active = "active",
  Disabled = "disabled"
};

onMounted(async () => {
  console.log("WN onMounted", store.state.configuration);
  workflowSteps.value = await workflow.getSteps();
});

const showRetirementAge = computed<boolean>(() => store.state.retiree != null && store.state.configuration.workflowShowsRetirementAge);
const impersonating = computed<boolean>(() => isImpersonating());
const stepCount = computed<number>(() => workflowSteps.value?.length ?? 0);
const showIcons = computed<boolean>(() => store.state.configuration?.workflow?.showIcons ?? false);

function getStageClass(stage: number, workflowStep: WorkflowStep): string {
      if (impersonating.value && store.state.transactionOngoing) {
        return MenuItemState.Disabled;
      }

      const currentStage = getCurrentStage();
      const isNextStep = stage == (currentStage + 1);
      const allowImmediateAccessToNextPage = workflowStep?.allowDirectAccessFromPreviousPage ?? false;

      if (isNextStep && allowImmediateAccessToNextPage) {
        const activeOrEnabled = stage == currentStage ? MenuItemState.Active : MenuItemState.Enabled;
        return activeOrEnabled;
      }

      if (stage < currentStage) {
        return MenuItemState.Enabled;
      } else if (stage == currentStage) {
        return MenuItemState.Active;
      } else {
        const enabledState = impersonating.value ? MenuItemState.Enabled : MenuItemState.Disabled;
        const isAccessible = isStageAccessible(stage, workflowStep);

        if (isAccessible) {
          return enabledState;
        }

        return `${enabledState} inaccessible`;
      }
}

function isStageAccessible(stage: number, workflowStep: WorkflowStep): boolean {
      if (impersonating.value && store.state.transactionOngoing) {
        return false;
      }

      const currentStage = getCurrentStage();
      const isNextStep = stage == (currentStage + 1);
      const allowImmediateAccessToNextPage = workflowStep?.allowDirectAccessFromPreviousPage ?? false;

      if (isNextStep && allowImmediateAccessToNextPage) {
        return stage == currentStage ? false : true;
      }

      if (stage < currentStage) {
        return true;
      } else if (stage == currentStage) {
        return false;
      } else if (stage > currentStage) {
        return impersonating.value ? true : false;
      }

      return true;
}

function getCurrentStage(): number {
  switch (props.workflowStage) {
    case "You":
      return 1;
    case "Life":
      return 2;
    case "Wants":
      return 3;
    case "Haves":
      return 4;
    case "Design":
      return 5;
    case "Test":
      return 6;
    case "Finish":
      return 7;
    case "Secure":
      return 8;
    default:
      return 1;
  }
}

function getNavIcon(view: View): Array<CompositeIconInfo> {
  const size = "2.2rem";
  const top = "-0.5rem";
  const left = "-1.12rem";

  switch (view) {
    case View.You:
      return [ { icon: "bi-person", size, top, left } ];
    case View.Life:
      return [ { icon: "bi-clipboard2-pulse", size, top, left } ];
    case View.Requirements:
      return [
        { icon: "bi-calendar", size, top, left },
        { icon: "bi-currency-pound", size: "1.4rem", top: "-0.0rem", left: "-0.74rem" }
      ];
    case View.Haves:
      return [ { icon: "bi-bank", size, top, left } ];
    case View.Design:
      return [ { icon: "bi-palette", size, top, left } ];
    case View.Test:
      return [ { icon: "bi-eyedropper", size, top, left } ];
    case View.Finish:
      return [ { icon: "bi-eye", size, top, left } ];
    case View.Secure:
      return [ { icon: "bi-lightbulb", size, top, left } ];
    default:
      return [ { icon: "bi-cloud", size, top, left } ];
  }
}

function changeLocation(event: any, newLocation: string, isStageAccessible: boolean) {
  event.preventDefault();

  if (newLocation.length === 0 || !isStageAccessible) {
    return;
  }

  workflow.goToWorkflowStep(newLocation);
}
</script>

<style scoped>
    ol.stepper {
        text-align: center;
        font-size: 1.75rem;
        line-height: 1.5rem;
        padding: 0;
        margin-bottom: 4rem;
        white-space: nowrap;
    }

        ol.stepper li:first-child {
            margin-left: 0;
        }

            ol.stepper li:first-child::before {
                background-color: transparent;
            }

        ol.stepper li {
            display: inline-block;
            border: 2px solid #0d2c41;
            border-radius: 50%;
            width: 4rem;
            height: 4rem;
            font-size: 100%;
            text-align: center;
            position: relative;
            margin: 0 1.5rem;
            background: #fff;
            cursor: pointer;
        }

    @media (min-width: 576px) {
        ol.stepper li {
            margin: 0 2.4rem;
        }
    }

    ol.stepper li a {
        display: inline-block;
        border-radius: 50%;
        width: 100%;
        height: 100%;
        padding-top: 1.1rem;
        text-decoration: none;
        color: #0d2c41;
    }

    ol.stepper li span {
        position: absolute;
        top: 4.5rem;
        left: -1.6rem;
        width: 7rem;
        font-size: 1rem;
        text-align: center;
        color: #0d2c41;
    }

    ol.stepper li.active {
        background: #178830;
        border-color: #178830;
        z-index: 1;
        pointer-events: none;
    }

    ol.stepper li.active a {
        color: #fff;
    }

    ol.stepper li.active ~ li::before {
        background: #d2d9dc;
    }

    ol.stepper li::before {
        content: "";
        position: absolute;
        top: 50%;
        left: -3.6rem;
        width: 3.6rem;
        height: 2px;
        background: #0d2c41;
        z-index: -1;
    }

    @media (min-width: 576px) {
        ol.stepper li::before {
            left: -5rem;
            width: 5rem;
            z-index: auto;
        }
    }

    ol.stepper li.disabled {
        border-color: #d2d9dc;
        background: none;
        background-color: #f7f7f7;
    }

        ol.stepper li.disabled a {
            color: #7e8f96;
        }

    ol.stepper li.inaccessible {
        cursor: default;
        pointer-events: none;
    }

    ol.stepper li:last-child {
        margin-right: 0;
    }

    ol.stepper li:focus .stepNumber {
      color: #178830 !important;
    }

    ol.stepper li.active ~li .stepNumber {
        color: #7e8f96;
    }

    .breadcrumb-mobile {
        margin: 25px 0;
        background-color: #bd4932;
        color: white;
        display: flex;
        justify-content: space-between;
        padding: 6px 8px;
        font-size: 20px;
        line-height: 25px;
    }

    .breadcrumb-mobile .breadcrumb-number {
        font-size: 18px;
        display: inline-block;
        text-align: center;
        font-weight: 400;
    }

    .breadcrumb-mobile .breadcrumb-text {
        padding-left: 8px;
        font-weight: 500;
    }
</style>